import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, addDays, differenceInDays } from 'date-fns';
import { es } from 'date-fns/locale';
import { Calendar as CalendarIcon, Users, Search, Package } from 'lucide-react';
import { Calendar } from './ui/calendar';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { WhatsAppIcon } from './BrandIcons';
import { whatsappLink } from '../lib/siteConfig';
import api from '../lib/api';

// Rangos de asistentes para cotizar un evento. Son texto libre a proposito:
// viajan tal cual dentro del mensaje de WhatsApp.
const EVENT_GUEST_RANGES = [
  '20 a 30 personas',
  '40 a 50 personas',
  '60 a 80 personas',
  '90 a 110 personas',
  '120 a 150 personas',
];

const EVENT_PACKAGES = [
  'Mobiliario Básico',
  'Mobiliario Madera',
  'Evento y hospedaje',
];

export default function BookingWidget({ variant = 'hero' }) {
  const navigate = useNavigate();
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [guests, setGuests] = useState('2');
  const [reservationType, setReservationType] = useState('hospedaje');
  const [blockedDates, setBlockedDates] = useState([]);
  const [checkInOpen, setCheckInOpen] = useState(false);
  const [checkOutOpen, setCheckOutOpen] = useState(false);

  // Estado del formulario de evento. Va aparte del de hospedaje para que al
  // cambiar de tipo y volver, cada formulario conserve lo que ya se habia
  // capturado en vez de perderse.
  const [eventPackage, setEventPackage] = useState('');
  const [eventGuests, setEventGuests] = useState('');
  const [eventDate, setEventDate] = useState(null);
  const [eventDateOpen, setEventDateOpen] = useState(false);

  useEffect(() => {
    fetchBlockedDates();
  }, []);

  const fetchBlockedDates = async () => {
    try {
      const response = await api.get('/blocked-dates');
      const dates = [];

      // Add blocked dates
      response.data.blocked?.forEach(b => {
        dates.push(new Date(b.date));
      });

      // Add reservation dates
      response.data.reservations?.forEach(r => {
        const start = new Date(r.check_in);
        const end = r.check_out ? new Date(r.check_out) : start;
        let current = start;
        while (current < end) {
          dates.push(new Date(current));
          current = addDays(current, 1);
        }
      });

      setBlockedDates(dates);
    } catch (error) {
      console.error('Error fetching blocked dates:', error);
    }
  };

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (checkIn) params.set('checkIn', format(checkIn, 'yyyy-MM-dd'));
    if (checkOut) params.set('checkOut', format(checkOut, 'yyyy-MM-dd'));
    params.set('guests', guests);
    params.set('type', reservationType);
    navigate(`/reservar?${params.toString()}`);
  };

  const isDateDisabled = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date < today) return true;

    return blockedDates.some(
      blocked => blocked.toDateString() === date.toDateString()
    );
  };

  // Para un evento solo se descarta el pasado: la fecha es una consulta, no
  // una reserva, y la disponibilidad se confirma por WhatsApp.
  const isPastDate = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const nights = checkIn && checkOut ? differenceInDays(checkOut, checkIn) : 0;

  const isHero = variant === 'hero';
  const isEvent = reservationType === 'evento';
  const isEventFormComplete = Boolean(eventPackage && eventGuests && eventDate);

  const handleEventWhatsApp = () => {
    if (!isEventFormComplete) return;

    const fecha = format(eventDate, "EEEE d 'de' MMMM 'de' yyyy", { locale: es });
    const message = [
      '¡Hola! Me gustaría cotizar un evento en La Finca',
      '',
      `*Paquete:* ${eventPackage}`,
      `*Número de personas:* ${eventGuests}`,
      `*Fecha del evento:* ${fecha}`,
      '',
      'Quedo al pendiente de la información. ¡Gracias!',
    ].join('\n');

    window.open(whatsappLink(message), '_blank', 'noopener,noreferrer');
  };

  return (
    <div
      data-testid="booking-widget"
      className={`${
        isHero
          ? 'bg-white/95 backdrop-blur-md border border-[#C8962D]/20 rounded-2xl p-6 shadow-[0_8px_30px_rgba(92,64,51,0.15)]'
          : 'bg-white rounded-xl p-4 border border-[#E0D8CC]'
      }`}
    >
      <div className={`grid gap-4 ${isHero ? 'md:grid-cols-5' : 'grid-cols-1'}`}>
        {/* Reservation Type */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-[#5C4033]">Tipo</label>
          <Select value={reservationType} onValueChange={setReservationType}>
            <SelectTrigger data-testid="reservation-type-select" className="border-[#E0D8CC]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="hospedaje">Hospedaje</SelectItem>
              <SelectItem value="dia">Day pass</SelectItem>
              <SelectItem value="evento">Evento</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {isEvent ? (
          <>
            {/* Paquete */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-[#5C4033]">Paquete</label>
              <Select value={eventPackage} onValueChange={setEventPackage}>
                <SelectTrigger data-testid="event-package-select" className="border-[#E0D8CC]">
                  <div className="flex items-center min-w-0">
                    <Package className="mr-2 h-4 w-4 text-[#C8962D] flex-shrink-0" />
                    <SelectValue placeholder="Seleccionar" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {EVENT_PACKAGES.map((pkg) => (
                    <SelectItem key={pkg} value={pkg}>
                      {pkg}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Asistentes */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-[#5C4033]">Personas</label>
              <Select value={eventGuests} onValueChange={setEventGuests}>
                <SelectTrigger data-testid="event-guests-select" className="border-[#E0D8CC]">
                  <div className="flex items-center min-w-0">
                    <Users className="mr-2 h-4 w-4 text-[#C8962D] flex-shrink-0" />
                    <SelectValue placeholder="Seleccionar" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {EVENT_GUEST_RANGES.map((range) => (
                    <SelectItem key={range} value={range}>
                      {range}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Fecha del evento */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-[#5C4033]">Fecha del evento</label>
              <Popover open={eventDateOpen} onOpenChange={setEventDateOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    data-testid="event-date-btn"
                    className="w-full justify-start text-left font-normal border-[#E0D8CC]"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4 text-[#C8962D]" />
                    {eventDate ? format(eventDate, 'dd MMM yyyy', { locale: es }) : 'Seleccionar'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    locale={es}
                    mode="single"
                    selected={eventDate}
                    onSelect={(date) => {
                      setEventDate(date);
                      setEventDateOpen(false);
                    }}
                    disabled={isPastDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Enviar por WhatsApp */}
            <div className={`${isHero ? 'flex items-end' : ''}`}>
              <Button
                onClick={handleEventWhatsApp}
                data-testid="event-whatsapp-btn"
                className="w-full bg-[#25D366] hover:bg-[#1EBE57] text-white h-10"
                disabled={!isEventFormComplete}
              >
                <WhatsAppIcon className="mr-2 h-4 w-4" />
                Cotizar
              </Button>
            </div>
          </>
        ) : (
          <>
            {/* Check-in */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-[#5C4033]">
                {reservationType === 'dia' ? 'Fecha' : 'Llegada'}
              </label>
              <Popover open={checkInOpen} onOpenChange={setCheckInOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    data-testid="checkin-date-btn"
                    className="w-full justify-start text-left font-normal border-[#E0D8CC]"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4 text-[#C8962D]" />
                    {checkIn ? format(checkIn, 'dd MMM yyyy', { locale: es }) : 'Seleccionar'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    locale={es}
                    mode="single"
                    selected={checkIn}
                    onSelect={(date) => {
                      setCheckIn(date);
                      setCheckInOpen(false);
                      if (reservationType === 'dia') {
                        setCheckOut(null);
                      } else if (date) {
                        setCheckOut(null);
                        setTimeout(() => setCheckOutOpen(true), 150);
                      }
                    }}
                    disabled={isDateDisabled}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Check-out (only for hospedaje) */}
            {reservationType === 'hospedaje' && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-[#5C4033]">Salida</label>
                <Popover open={checkOutOpen} onOpenChange={setCheckOutOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      data-testid="checkout-date-btn"
                      className="w-full justify-start text-left font-normal border-[#E0D8CC]"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4 text-[#C8962D]" />
                      {checkOut ? format(checkOut, 'dd MMM yyyy', { locale: es }) : 'Seleccionar'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      locale={es}
                      mode="single"
                      selected={checkOut}
                      onSelect={(date) => {
                        setCheckOut(date);
                        setCheckOutOpen(false);
                      }}
                      disabled={(date) => {
                        if (isDateDisabled(date)) return true;
                        if (checkIn && date <= checkIn) return true;
                        return false;
                      }}
                      defaultMonth={checkIn || undefined}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            )}

            {/* Guests */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-[#5C4033]">Huéspedes</label>
              <Select value={guests} onValueChange={setGuests}>
                <SelectTrigger data-testid="guests-select" className="border-[#E0D8CC]">
                  <div className="flex items-center">
                    <Users className="mr-2 h-4 w-4 text-[#C8962D]" />
                    <SelectValue />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {[...Array(10)].map((_, i) => (
                    <SelectItem key={i + 1} value={String(i + 1)}>
                      {i + 1} {i === 0 ? 'huésped' : 'huéspedes'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Search Button */}
            <div className={`${isHero ? 'flex items-end' : ''}`}>
              <Button
                onClick={handleSearch}
                data-testid="search-availability-btn"
                className="w-full bg-[#5C4033] hover:bg-[#3D5A3E] text-white h-10"
                disabled={!checkIn || (reservationType === 'hospedaje' && !checkOut)}
              >
                <Search className="mr-2 h-4 w-4" />
                {isHero ? 'Buscar' : 'Buscar Disponibilidad'}
              </Button>
            </div>
          </>
        )}
      </div>

      {/* Nights indicator */}
      {!isEvent && nights > 0 && (
        <p className="text-sm text-[#5C4033] mt-3 text-center">
          {nights} {nights === 1 ? 'noche' : 'noches'} seleccionadas
        </p>
      )}

      {isEvent && (
        <p className="text-xs text-[#1A1A1A]/50 mt-3 text-center">
          Te abriremos WhatsApp con el mensaje listo para enviar.
        </p>
      )}
    </div>
  );
}
